import { Button, Typography } from '@execonline-inc/execonline-ui';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { observer } from 'mobx-react';
import { useContext } from 'react';
import { T, TranslationsContext, translation } from '../../../../../../Translations';
import { AnchoredModal } from '../components/AnchoredModal';

interface ChildrenProps {
  onOpen: () => void;
}

interface Props {
  sectionName: string;
  onConfirm: () => void;
  children: (props: ChildrenProps) => React.ReactNode;
}

function DeleteSectionModal({ sectionName, onConfirm, children }: Props) {
  const ts = useContext(TranslationsContext);
  return (
    <AnchoredModal
      placement="bottom-end"
      closeOnContentClick={false}
      showArrow={false}
      aria-labelledby="delete-section-modal-title"
      children={({ onClose }) => (
        <div className="relative px-5 pt-2 pb-5 w-[320px]">
          <button
            type="button"
            onClick={onClose}
            className="absolute -top-1 -right-1 p-1 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={translation('Close dialog')(ts)}
          >
            <XMarkIcon className="w-6 h-6" aria-hidden="true" />
          </button>

          <div className="mb-3">
            <Typography id="delete-section-modal-title" variant="h6" fontWeight="semibold">
              <T kind="Remove Section" />
            </Typography>
          </div>

          <hr className="border-border mb-4" />

          <Typography variant="bodyMedium" className="mb-4">
            {sectionName}
          </Typography>

          <div className="flex justify-end gap-2">
            <Button
              variant="text"
              color="secondary"
              onClick={onClose}
              data-intercom-target="builder-v2-delete-section-cancel-button"
            >
              <T kind="Cancel" />
            </Button>
            <Button
              color="error"
              onClick={() => {
                onConfirm();
                onClose();
              }}
              data-intercom-target="builder-v2-delete-section-confirm-button"
            >
              <T kind="Remove" />
            </Button>
          </div>
        </div>
      )}
      renderTrigger={(ref, getReferenceProps) => {
        const props = getReferenceProps();
        const onClick = props.onClick as () => void;
        return <div ref={ref}>{children({ onOpen: onClick })}</div>;
      }}
    />
  );
}

export default observer(DeleteSectionModal);
